/**
 * PublicLeadFormPage — unauthenticated lead-capture landing page at /f/:slug.
 *
 * This is where QR Studio codes with a "form" destination land (via the
 * fhl.one/q/{code} tracking redirect, which appends ?qs={scanId} so the
 * eventual submission is attributed to the exact scan). The page also works
 * when the /f/{slug} link is shared directly — scan attribution is simply
 * absent.
 *
 * Reads the crm_lead_forms row with the anon key (SELECT-open table, matching
 * booking pages) and renders it with LeadFormRenderer — the same component
 * the studio's builder preview uses, so what the LO previews is literally
 * what the visitor gets.
 *
 * Submissions POST to /api/forms/submit (firebase.json hosting rewrite ->
 * qrFormSubmitHTTP, same-origin in production; direct function URL in local
 * dev). Post-submit behavior comes from the form config:
 *   message  — inline thank-you panel
 *   redirect — hard navigate to an external URL
 *   booking  — SPA-push to the LO's Origin booking page at /book/{handle}/
 *              {slug} with ?name=&email=&phone= prefilled from the answers
 *              ("fill this out, then grab a time with me")
 */

import React, { useState, useEffect, useMemo } from 'react';
import { useParams, useLocation, useHistory } from 'react-router-dom';
import {
  Box,
  Center,
  Heading,
  Text,
  Spinner,
} from '@chakra-ui/react';
import { supabase } from '../../utils/supabase';
import LeadFormRenderer, {
  shadeHex,
  SuccessPanel,
  extractContactPrefill,
} from '../../components/LeadForm/LeadFormRenderer';

const SUBMIT_URL = window.location.hostname === 'localhost'
  ? 'https://us-east1-future-operations-hub.cloudfunctions.net/qrFormSubmitHTTP'
  : '/api/forms/submit';

function FormNotFound() {
  return (
    <Center minH="100vh" bg="gray.50" flexDir="column" px={4}>
      <Heading size="md" mb={2} color="gray.700">This form isn't available</Heading>
      <Text color="gray.500" textAlign="center">
        The link may be incorrect or the form has been turned off. Please reach out to your loan officer directly.
      </Text>
    </Center>
  );
}

export default function PublicLeadFormPage() {
  const { slug } = useParams();
  const location = useLocation();
  const history = useHistory();

  const [form, setForm] = useState(null);
  const [status, setStatus] = useState('loading'); // loading | ready | notfound
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [phase, setPhase] = useState('form'); // form | thanks | redirecting

  // Scan attribution: /q/{code} appends ?qs={scanId} on its way here.
  const scanId = useMemo(
    () => new URLSearchParams(location.search).get('qs') || null,
    [location.search]
  );

  useEffect(() => {
    let cancelled = false;
    (async () => {
      const { data, error } = await supabase
        .from('crm_lead_forms')
        .select('*')
        .eq('slug', slug)
        .maybeSingle();
      if (cancelled) return;
      if (!error && data && data.is_active) {
        setForm(data);
        setStatus('ready');
        document.title = data.headline || data.title || 'Future Home Loans';
      } else {
        setStatus('notfound');
      }
    })();
    return () => { cancelled = true; };
  }, [slug]);

  const handleSubmit = async (answers, { hp }) => {
    setIsSubmitting(true);
    try {
      const resp = await fetch(SUBMIT_URL, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ formId: form.id, answers, scanId, hp }),
      });
      const json = await resp.json().catch(() => ({}));
      if (!resp.ok || !json.success) {
        throw new Error(json.error || 'Your submission could not be sent. Please try again.');
      }

      // Post-submit behavior per the form config
      if (form.post_submit_type === 'redirect' && form.post_submit_redirect_url) {
        setPhase('redirecting');
        window.location.assign(form.post_submit_redirect_url);
        return;
      }

      if (form.post_submit_type === 'booking' && form.post_submit_booking_page_id) {
        const { data: page } = await supabase
          .from('crm_booking_pages')
          .select('owner_handle, slug, is_active')
          .eq('id', form.post_submit_booking_page_id)
          .maybeSingle();
        if (page && page.is_active) {
          const prefill = extractContactPrefill(form.fields, answers);
          const params = new URLSearchParams();
          if (prefill.name) params.set('name', prefill.name);
          if (prefill.email) params.set('email', prefill.email);
          if (prefill.phone) params.set('phone', prefill.phone);
          const query = params.toString();
          history.push(`/book/${page.owner_handle}/${page.slug}${query ? `?${query}` : ''}`);
          return;
        }
        // Booking page vanished — fall through to the thank-you panel.
      }

      setPhase('thanks');
      window.scrollTo(0, 0);
    } finally {
      setIsSubmitting(false);
    }
  };

  if (status === 'loading') {
    return (
      <Center minH="100vh" bg="gray.50">
        <Spinner size="xl" color="blue.500" thickness="4px" />
      </Center>
    );
  }

  if (status === 'notfound') {
    return <FormNotFound />;
  }

  const accent = /^#[0-9a-fA-F]{6}$/.test(form.accent_color || '') ? form.accent_color : '#1A365D';

  if (phase === 'redirecting') {
    return (
      <Center minH="100vh" bg="gray.50" flexDir="column">
        <Spinner size="xl" thickness="4px" style={{ color: shadeHex(accent, -5) }} />
        <Text mt={4} color="gray.500">One moment...</Text>
      </Center>
    );
  }

  if (phase === 'thanks') {
    return (
      <SuccessPanel
        accent={accent}
        title="You're all set!"
        body={form.post_submit_message || 'Thanks! Your information has been received.'}
      />
    );
  }

  return (
    <Box>
      <LeadFormRenderer form={form} onSubmit={handleSubmit} isSubmitting={isSubmitting} />
    </Box>
  );
}
